import type { DefaultValues } from 'react-hook-form';
import { dayjs, formatThousands } from '@noor/lib';
import type { MenuProduct } from '@/entities/menu';
import type { Promotion, PromotionDetail } from '@/entities/promotion';
import type {
  AddPromotionPayload,
  BranchSelections,
  DiscountType,
  EditPromotionPayload,
  PromotionFormValues,
} from '../model/schema';
import type { UpsertPromotionVars } from '../model/use-upsert-promotion';

type PromotionPayload = AddPromotionPayload | EditPromotionPayload;

/**
 * Seed the form from the edit target, or empty defaults for a create.
 *
 * `detail` is the promotion's OWN record, read by id. When present it wins
 * throughout, because the `payload.promotion` row is a joined projection built
 * for the table: it names its product and branch by display name, so seeding
 * from it has to match those back to ids by string — which silently picks the
 * wrong product when two share a name, and picks NOTHING when the row's name
 * was localized differently than the option list's. The row remains the
 * fallback so the form is populated while the read is still in flight.
 */
export function buildDefaults(
  payload: PromotionPayload,
  detail?: PromotionDetail,
): DefaultValues<PromotionFormValues> {
  if (payload.mode === 'edit') {
    const { promotion } = payload;

    if (detail) {
      const start = parseIsoDate(detail.startsAt);
      const end = parseIsoDate(detail.endsAt);

      /**
       * The by-id read wins per FIELD, not wholesale.
       *
       * `restaurant_id` and `product_ids` both `.default()` to empty in the
       * wire schema, so a response that omits either (protobuf-JSON drops
       * zero values) mapped to `''` / `[]` — and overriding the row with that
       * is what blanked the branch select on every edit, and dropped the
       * promo's products. The ROW is the fallback for exactly the fields the
       * detail could not answer.
       *
       * Both fall back to the row's own IDS, never to matching its display
       * names against the option lists. A branch or product name is
       * locale-resolved server-side, so a row resolved in one language could
       * not be matched against options resolved in another.
       */
      const branchId = detail.restaurantId || branchIdsFromRow(payload, promotion)[0];
      const productIds =
        detail.productIds.length > 0
          ? detail.productIds
          : productIdsFromRow(payload.products, promotion);

      return {
        nameRu: detail.nameTranslations.ru,
        nameUz: detail.nameTranslations.uz,
        nameEn: detail.nameTranslations.en,
        // The detail read is BRANCH-SCOPED, so it can only ever seed the one
        // branch it was read for. Other branches fill in as the merchant opens
        // them (see `mergeBranchSelection`) rather than being guessed here.
        selections: branchId ? { [branchId]: productIds } : {},
        discountType: detail.discountType,
        discountValue: detail.discountValue,
        ...(start ? { startDate: start } : {}),
        ...(end ? { endDate: end } : {}),
      };
    }

    const [start, end] = parseDuration(promotion.duration);
    const rowBranchId = branchIdsFromRow(payload, promotion)[0];
    return {
      // The CAMPAIGN's name, locale-resolved — not the product's. This used to
      // seed `productName`, which meant a campaign called «Навруз» covering
      // «Медовик» opened with "Медовик" in the name field, and saving without
      // touching it renamed the campaign after one of its products.
      //
      // Ru only: the row carries one resolved string (the backend's own
      // canonical fallback), so uz/en stay blank until the by-id read lands
      // with the real translations. Echoing the resolved value into them would
      // present a fallback as a real translation — and write it back on save.
      nameRu: promotion.name,
      nameUz: '',
      nameEn: '',
      selections: rowBranchId
        ? { [rowBranchId]: productIdsFromRow(payload.products, promotion) }
        : {},
      discountType: promotion.discountType,
      discountValue:
        promotion.discountType === 'fixed'
          ? promotion.finalPrice
          : promotion.discountPercent,
      ...(start ? { startDate: start } : {}),
      ...(end ? { endDate: end } : {}),
    };
  }
  return {
    nameRu: '',
    nameUz: '',
    nameEn: '',
    selections: {},
    discountType: 'percent',
  };
}

/**
 * Fold one branch's freshly-read product set into the running selection.
 *
 * The edit wizard reads a branch's products only when the merchant OPENS that
 * branch (the endpoint answers per branch), so the selection fills in
 * progressively. A branch the merchant has already touched is left alone —
 * re-seeding it from a late-arriving read would silently undo their ticks.
 */
export function mergeBranchSelection(
  selections: BranchSelections,
  branchId: string,
  productIds: string[],
): BranchSelections {
  if (branchId in selections) return selections;
  return { ...selections, [branchId]: productIds };
}

/** An ISO instant from the wire as a `Date`, or undefined if absent/unparseable. */
export function parseIsoDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Build the mutation vars from the validated form. On create the product ×
 * branch selection fans out into one input per pair; edit patches the existing
 * promotion's terms and reports which products the merchant unchecked.
 *
 * `detail` is the promotion's stored record — the only thing that knows which
 * products it currently covers, since the table row names just one. Without it
 * an edit cannot tell an unchecked product from one that was never on the
 * campaign, so the removal diff is simply empty and the terms patch still
 * applies.
 */
export function toUpsertVars(
  values: PromotionFormValues,
  payload: PromotionPayload,
  duration: string | null,
  detail?: PromotionDetail,
  /**
   * Products the merchant actually picked from, keyed by branch.
   *
   * The wizard's step 2 lists what each branch STOCKS (a per-branch
   * `RestaurantProduct` overlay), which is not the merchant-wide
   * `payload.products` — a product sold at one branch and not another appears
   * in one list and not the other, and its PRICE differs per branch too. Given
   * this map, a picked product resolves against the branch it was picked at;
   * without it (the mobile sheet, which has no per-branch lists) the payload's
   * catalogue is the fallback.
   */
  productsByBranch?: Record<string, MenuProduct[]>,
  /**
   * The branch `detail` was READ FOR — the only branch whose stored product set
   * is known, and so the only one the removal diff may be scoped to.
   *
   * Passed in rather than read off `detail.restaurantId`, which this endpoint
   * answers with `''`. Omitted means "no branch known", and nothing is unlinked.
   */
  readBranchId?: string,
): UpsertPromotionVars {
  const products = payload.products;
  // The backend stores the window as RFC3339 instants; the form holds `Date`s.
  // Both ends are required by the schema, so both are always sent — there is
  // no open-ended promotion to clear a bound for any more.
  const startsAt = values.startDate.toISOString();
  const endsAt = values.endDate.toISOString();
  const nameTranslations = {
    ru: values.nameRu,
    uz: values.nameUz,
    en: values.nameEn,
  };

  // One input per (branch, product) PAIR — the shape a table row has. The API
  // layer transposes them back into the wire's per-branch `selections`.
  //
  // Built by walking the selection map rather than a cross product, which is
  // the whole point of the redesign: branch A may carry three products while
  // branch B carries one.
  const inputs = Object.entries(values.selections).flatMap(([branchId, productIds]) => {
    const branch = payload.branches.find((item) => item.id === branchId);
    if (!branch) return [];
    // Prefer the branch's own stocked list — that is where the merchant picked
    // from, and it carries the branch's overlay price. Falling back to the
    // merchant-wide catalogue keeps callers without per-branch lists working.
    const branchCatalogue = productsByBranch?.[branchId] ?? products;
    return productIds.flatMap((productId) => {
      const product =
        branchCatalogue.find((p) => String(p.id) === productId) ??
        products.find((p) => String(p.id) === productId);
      // A picked id that resolves in NEITHER list would be dropped silently —
      // it cannot happen from the UI (the merchant picks from one of them), and
      // sending an unresolvable id would fail the whole batch anyway.
      if (!product) return [];
      const { discountPercent, finalPrice } = resolveDiscount(
        product.price,
        values.discountType,
        values.discountValue,
      );
      return [
        {
          productId: String(product.id),
          productName: product.name,
          nameTranslations,
          restaurantId: branch.id,
          branchName: branch.name,
          price: product.price,
          discountType: values.discountType,
          discountPercent,
          finalPrice,
          duration,
          startsAt,
          endsAt,
          ...(product.imageUrl ? { imageUrl: product.imageUrl } : {}),
        },
      ];
    });
  });

  if (payload.mode === 'edit') {
    // Products the campaign covers AT THE READ BRANCH that the merchant just
    // unchecked. The terms PATCH cannot express a narrower product set — it
    // only writes the discount and window — so the removal is its own call
    // (`DELETE /promotions/{id}/products`), and without it unchecking a
    // product silently did nothing.
    //
    // Scoped to the branch the detail was READ FOR, since that is the only
    // branch whose stored product set is known: the endpoint answers per
    // branch, so diffing against one we never read would "remove" products
    // merely because we never learned they were there.
    //
    // That branch is `readBranchId`, passed in BY THE CALLER — it is not
    // `detail.restaurantId`, which this endpoint returns as `''`. Deriving it
    // from the response made `keptAtReadBranch` permanently empty, so EVERY
    // product the campaign covered was queued for unlinking on any save.
    const keptAtReadBranch = new Set(
      readBranchId ? (values.selections[readBranchId] ?? []) : [],
    );
    const removedProductIds = readBranchId
      ? (detail?.productIds ?? []).filter((id) => !keptAtReadBranch.has(id))
      : [];

    return {
      id: payload.promotion.id,
      inputs: inputs.slice(0, 1),
      ...(removedProductIds.length > 0 && readBranchId
        ? { removedProductIds, restaurantId: readBranchId }
        : {}),
    };
  }
  return { inputs };
}

/**
 * The lowest product price a FIXED promo price must stay under, or `undefined`
 * when the entered price is acceptable for every picked product.
 *
 * A fixed price at or above a product's price is not a discount, and the
 * backend rejects it with an opaque error. The baseline is the pre-discount
 * price when the product already carries a promotion (its `price` is then the
 * discounted one), so re-saving a running campaign is not flagged.
 */
export function fixedPriceCeiling(
  values: PromotionFormValues,
  payload: AddPromotionPayload | EditPromotionPayload,
  productsByBranch?: Record<string, MenuProduct[]>,
): number | undefined {
  if (values.discountType !== 'fixed') return undefined;
  let ceiling: number | undefined;
  for (const [branchId, productIds] of Object.entries(values.selections)) {
    const catalogue = productsByBranch?.[branchId] ?? payload.products;
    for (const productId of productIds) {
      const product =
        catalogue.find((p) => String(p.id) === productId) ??
        payload.products.find((p) => String(p.id) === productId);
      if (!product) continue;
      const baseline = product.originalPrice ?? product.price;
      if (baseline > 0 && values.discountValue >= baseline)
        ceiling = ceiling === undefined ? baseline : Math.min(ceiling, baseline);
    }
  }
  return ceiling;
}

/**
 * Derive the stored `discountPercent` + `finalPrice` from the entered value.
 *
 * `price` is the product's CURRENT catalog price, which for a product already
 * carrying a promotion is the DISCOUNTED one (`MenuProduct.price` resolves to
 * `effective_price`). That made the old `Math.min(value, price)` clamp wrong in
 * both directions on an edit: raising a fixed price above the running discount
 * silently snapped it back down — the entered amount "wouldn't save" — and the
 * percentage it derived from the clamped pair came out 0, which is the «0%» the
 * table then showed.
 *
 * A fixed promo is therefore stored EXACTLY as entered. The percentage is a
 * derived display value, computed only when there is a sane baseline to derive
 * it from, and never used to overwrite what the merchant typed.
 */
function resolveDiscount(
  price: number,
  type: DiscountType,
  value: number,
): { discountPercent: number; finalPrice: number } {
  if (type === 'fixed') {
    // Store the entered amount verbatim — see above.
    const finalPrice = value;
    // Only meaningful when the new price is genuinely below the baseline;
    // otherwise there is no discount to express as a percentage.
    const discountPercent =
      price > 0 && finalPrice < price
        ? Math.round(((price - finalPrice) / price) * 100)
        : 0;
    return { discountPercent, finalPrice };
  }
  const discountPercent = Math.min(value, 100);
  const finalPrice = Math.round(price * (1 - discountPercent / 100));
  return { discountPercent, finalPrice };
}

/**
 * The edited row's product, as an option id.
 *
 * Taken from the row's OWN `productId` — it is the joined row's real id, so
 * this needs no matching at all. It is still checked against the option list,
 * because selecting an id the multi-select does not offer would render an
 * empty chip; a product missing from the list (filtered, or from another
 * category) falls back to matching its display name.
 */
function productIdsFromRow(products: MenuProduct[], promotion: Promotion): string[] {
  if (products.some((p) => String(p.id) === promotion.productId)) {
    return [promotion.productId];
  }
  const match = products.find((p) => p.name === promotion.productName);
  return match ? [String(match.id)] : [];
}

/** Same, for the row's branch. */
function branchIdsFromRow(payload: PromotionPayload, promotion: Promotion): string[] {
  if (payload.branches.some((branch) => branch.id === promotion.restaurantId)) {
    return [promotion.restaurantId];
  }
  const match = payload.branches.find((branch) => branch.name === promotion.branchName);
  return match ? [match.id] : [];
}

/** Row subtitle in the docked product panel — `Круглые торты · 1 000 000 сум`. */
export function buildProductDescription(
  categoryName: string | undefined,
  price: number,
  currency: string,
): string {
  const amount = `${formatThousands(price)} ${currency}`;
  return categoryName ? `${categoryName} · ${amount}` : amount;
}

/** Trigger label for a picked date — `01.01.2025`, matching the Figma. */
export function formatDateLabel(date: Date): string {
  return dayjs(date).format('DD.MM.YYYY');
}

/** `"01.01.2025 - 30.05.2025"` → start/end `Date`s (undefined when unparseable). */
function parseDuration(duration: string | null): [Date | undefined, Date | undefined] {
  if (!duration) return [undefined, undefined];
  const [rawStart, rawEnd] = duration.split(/\s*[-–]\s*/);
  const parse = (raw?: string): Date | undefined => {
    const d = dayjs(raw, 'DD.MM.YYYY');
    return raw && d.isValid() ? d.toDate() : undefined;
  };
  return [parse(rawStart), parse(rawEnd)];
}

/** Build the preformatted `"DD.MM.YYYY - DD.MM.YYYY"` range stored on the row. */
export function formatDuration(start?: Date, end?: Date): string | null {
  if (!start || !end) return null;
  const from = dayjs(start);
  const to = dayjs(end);
  if (!from.isValid() || !to.isValid()) return null;
  return `${from.format('DD.MM.YYYY')} - ${to.format('DD.MM.YYYY')}`;
}
